import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  antigenApi,
  contraindicationApi,
  vaccineProductApi,
} from "api/immunization";
import { CONTRAINDICATION_TYPES, humanise } from "config/immunizationConstants";
import MasterModalShell from "./common/MasterModalShell";
import CodeField from "./common/CodeField";
import { validateCode, validateRequired } from "./common/validation";

const toIdList = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => (typeof item === "string" ? item : item?._id))
    .filter(Boolean);

const ContraindicationModal = ({ open, onClose, data }) => {
  const recordId = data?._id;
  const isUpdate = Boolean(recordId);

  const [form, setForm] = useState({});
  const [antigens, setAntigens] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      code: data?.code || "",
      conditionName: data?.conditionName || "",
      type: data?.type || "PRECAUTION",
      appliesToAntigenIds: toIdList(data?.appliesToAntigenIds),
      appliesToProductIds: toIdList(data?.appliesToProductIds),
      appliesToAll: Boolean(data?.appliesToAll),
      isTemporary: Boolean(data?.isTemporary),
      deferralDays: data?.deferralDays ?? "",
      rationale: data?.rationale || "",
      clinicalGuidance: data?.clinicalGuidance || "",
      overridePermitted:
        data?.overridePermitted === undefined ? true : data.overridePermitted,
      sequence: data?.sequence ?? 0,
    });
  }, [open, data]);

  useEffect(() => {
    if (!open) return;

    antigenApi
      .active()
      .then((res) => setAntigens(res?.data?.data || []))
      .catch(() => toast.error("Could not load antigens"));

    vaccineProductApi
      .active()
      .then((res) => setProducts(res?.data?.data || []))
      .catch(() => toast.error("Could not load vaccine products"));
  }, [open]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const validationError = useMemo(() => {
    const base =
      validateCode(form.code, { isUpdate, label: "Contraindication code" }) ||
      validateRequired(form.conditionName, "Condition name");
    if (base) return base;

    const hasTarget =
      form.appliesToAll ||
      (form.appliesToAntigenIds || []).length > 0 ||
      (form.appliesToProductIds || []).length > 0;
    if (!hasTarget) {
      return "Target at least one antigen or product, or tick 'Applies to all vaccines'";
    }

    if (form.type === "ABSOLUTE" && form.overridePermitted) {
      return "An absolute contraindication cannot permit an override";
    }
    if (form.isTemporary && form.deferralDays === "") {
      return "A temporary contraindication must define a deferral period";
    }
    if (!form.isTemporary && form.deferralDays !== "") {
      return "A deferral period is only valid on a temporary contraindication";
    }

    return "";
  }, [form, isUpdate]);

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);

    const payload = {
      conditionName: form.conditionName.trim(),
      type: form.type,
      appliesToAntigenIds: form.appliesToAntigenIds,
      appliesToProductIds: form.appliesToProductIds,
      appliesToAll: form.appliesToAll,
      isTemporary: form.isTemporary,
      deferralDays: form.deferralDays === "" ? null : Number(form.deferralDays),
      rationale: form.rationale?.trim() || "",
      clinicalGuidance: form.clinicalGuidance?.trim() || "",
      overridePermitted: form.overridePermitted,
      sequence: Number(form.sequence) || 0,
    };

    try {
      if (isUpdate) {
        await contraindicationApi.update(recordId, payload);
        toast.success("Contraindication updated successfully");
      } else {
        await contraindicationApi.create({
          ...payload,
          code: form.code.trim(),
        });
        toast.success("Contraindication created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} contraindication`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterModalShell
      open={open}
      onClose={onClose}
      isUpdate={isUpdate}
      entityLabel="Contraindication"
      saving={saving}
      validationError={validationError}
      onSubmit={handleSubmit}
      maxWidth="md"
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <CodeField
          value={form.code}
          onChange={(value) => set({ code: value })}
          isUpdate={isUpdate}
          label="Code"
        />
        <TextField
          label="Condition Name"
          required
          fullWidth
          size="small"
          value={form.conditionName || ""}
          onChange={(event) => set({ conditionName: event.target.value })}
        />
        <TextField
          select
          label="Type"
          size="small"
          sx={{ minWidth: 170 }}
          value={form.type || "PRECAUTION"}
          onChange={(event) => {
            const type = event.target.value;
            set({
              type,
              ...(type === "ABSOLUTE" ? { overridePermitted: false } : {}),
            });
          }}
        >
          {CONTRAINDICATION_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type === "FALSE" ? "False (myth)" : humanise(type)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={antigens}
        disabled={form.appliesToAll}
        value={antigens.filter((a) =>
          (form.appliesToAntigenIds || []).includes(a._id),
        )}
        getOptionLabel={(option) => option?.antigenName || ""}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        onChange={(event, next) =>
          set({ appliesToAntigenIds: next.map((item) => item._id) })
        }
        renderInput={(params) => (
          <TextField {...params} label="Applies To Antigens" />
        )}
      />

      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={products}
        disabled={form.appliesToAll}
        value={products.filter((p) =>
          (form.appliesToProductIds || []).includes(p._id),
        )}
        getOptionLabel={(option) => option?.brandName || ""}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        onChange={(event, next) =>
          set({ appliesToProductIds: next.map((item) => item._id) })
        }
        renderInput={(params) => (
          <TextField {...params} label="Applies To Products" />
        )}
      />

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.appliesToAll)}
              onChange={(event) => {
                const checked = event.target.checked;
                set({
                  appliesToAll: checked,
                  ...(checked ?
                    { appliesToAntigenIds: [], appliesToProductIds: [] }
                  : {}),
                });
              }}
            />
          }
          label="Applies to all vaccines"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.isTemporary)}
              onChange={(event) => {
                const checked = event.target.checked;
                set({
                  isTemporary: checked,
                  ...(checked ? {} : { deferralDays: "" }),
                });
              }}
            />
          }
          label="Temporary"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.overridePermitted)}
              disabled={form.type === "ABSOLUTE"}
              onChange={(event) =>
                set({ overridePermitted: event.target.checked })
              }
            />
          }
          label="Override permitted"
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          label="Deferral Days"
          type="number"
          size="small"
          sx={{ minWidth: 170 }}
          disabled={!form.isTemporary}
          value={form.deferralDays}
          onChange={(event) => set({ deferralDays: event.target.value })}
        />
        <TextField
          label="Sequence"
          type="number"
          size="small"
          sx={{ minWidth: 120 }}
          value={form.sequence}
          onChange={(event) => set({ sequence: event.target.value })}
        />
      </Box>

      <TextField
        label="Rationale"
        fullWidth
        size="small"
        multiline
        minRows={2}
        value={form.rationale || ""}
        onChange={(event) => set({ rationale: event.target.value })}
      />

      <TextField
        label="Clinical Guidance"
        fullWidth
        size="small"
        multiline
        minRows={2}
        value={form.clinicalGuidance || ""}
        onChange={(event) => set({ clinicalGuidance: event.target.value })}
      />
    </MasterModalShell>
  );
};

export default ContraindicationModal;
